import {
    IContextualMenuItem, IOverflowSetItemProps, Separator, Stack,
    Text,
    useTheme
} from "@fluentui/react";
import { AddIcon } from '@fluentui/react-icons-mdl2';
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import LinkMenu from "components/controls/LinkMenu";
import SimpleCard from "components/controls/SimpleCard";
import { AppLinkTo, EViewType } from "global/Application";
import { useApplication } from "global/Application/Context";
import { removeBenefitReference } from "global/Reducer/dataSlice";
import { updateWorkspace } from "global/Reducer/statusSlice";
import * as FAM from "model/FlexibleAttributeModel";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import BenefitCard, { EBenefitCardFunction } from "views/BlueRelationship/BenefitCard";
import BenefitGrid from "../BenefitGrid";

interface IBenefitPanelProps {
    benefits?: FAM.IBenefit[];
    benefitsPlan?: FAM.IPlan;
    onAdd?: () => void;
}

const BenefitPanel: React.FunctionComponent<IBenefitPanelProps> = (props) => {

    const application = useApplication();
    const dispatch = useDispatch();
    const theme = useTheme();
    const state = useSelector((state: RootState) => state);
    const history = useHistory();


    const workspace = state.status.workspace;
    const disabled = state.status.viewType !== EViewType.Modify;

    const benefits: FAM.IBenefit[] = [];

    if (props.benefitsPlan) {
        state.data.json.benefits.forEach(eachBenefit => {
            if (props.benefitsPlan?.benefitRefs.find(q => q.id === eachBenefit.id)) {
                benefits.push(eachBenefit)
            }
        });
    } else if (props.benefits)
        benefits.push(...props.benefits);
    else
        benefits.push(...state.data.json.benefits);

    if (workspace.benefit === "cardSorted")
        benefits.sort((a, b) => a.bcbs_name?.localeCompare(b.bcbs_name!) || a.name.localeCompare(b.name));


    const menuItemsBenefit = useMemo(() => {
        const menuItems: IContextualMenuItem[] = [
            {
                key: 'card',
                text: 'Card View - Sorted By Entry Sequence',
            },
            {
                key: 'cardSorted',
                text: 'Card View - Sorted By Category/Name',
            },
            {
                key: 'grid',
                text: 'Grid View',
            },
        ];
        return menuItems;
    }, []);


    function onRenderBenefit() {
        const items: IContextualMenuItem[] = [];

        if (props.onAdd)
            if (!disabled && application.ChangeTypePermissions.canAddBenefit)
                items.push({
                    key: 'groupConfiguration.benefits.add',
                    name: 'Add',
                    icon: 'DocumentManagement',
                    onClick: (event, item) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                        props.onAdd?.()
                    },
                },);

        return <CustomPanel title={"Benefits"}
            canCollapse={true}
            defaultCollapsed={true}
            contentStyle={{ overflow: 'visible' }}
            storageId={"groupConfiguration.benefits"}
            items={items}
            mountWhenExpanded={true}
        >
            <LinkMenu
                styles={{ root: { marginTop: '12px' } }}
                menuItems={menuItemsBenefit}
                selectedKey={workspace.benefit}
                onChange={(event, menuItem) => {
                    dispatch(updateWorkspace({ 'benefit': menuItem.key }));
                }}
            />
            <Separator />
            {workspace.benefit === "grid" && onRenderBenefitGrid()}
            {workspace.benefit === "card" && onRenderBenefitCard()}
            {workspace.benefit === "cardSorted" && onRenderBenefitCard()}
        </CustomPanel>;
    }

    function onRenderBenefitCard() {

        return <Stack
            horizontal
            wrap
            verticalFill
            tokens={{ childrenGap: 15 }}
            horizontalAlign="start"
            style={{ padding: '15px', marginBottom: '15px' }}
        >
            {benefits.map(eachBenefit => {

                // skip deactivated records/only show deactivated records if permitted at group config properties level
                if (eachBenefit.deactivated !== state.data.json.showInActives?.value)
                    return <></>;

                const menu: IOverflowSetItemProps[] = [];

                if (props.benefitsPlan)
                    if (state.status.viewType === EViewType.Modify && application.ChangeTypePermissions.canLinkPlanBenefit)
                        menu.push({
                            key: 'remove', name: 'Remove', icon: 'Edit',
                            onClick: () => {
                                dispatch(removeBenefitReference({
                                    benefitRef: eachBenefit,
                                    planRef: props.benefitsPlan!
                                }));
                            }
                        });

                return (
                    <BenefitCard
                        key={eachBenefit.id}
                        benefit={eachBenefit}
                        state={state}
                        overflowItems={menu}
                        mode={EBenefitCardFunction.display}
                        contextPlans={state.data.json.plans}
                        onClick={() => history.push(AppLinkTo(state,
                            {
                                id: eachBenefit.id,
                                type: 'benefit',
                                // parent: props.benefitsPlan
                                //     ? {
                                //         id: props.benefitsPlan.id,
                                //         type: 'plan'
                                //     }
                                //     : undefined
                            }
                        ))}
                    />
                );
            })}
            {!disabled && application.ChangeTypePermissions.canAddBenefit &&
                <SimpleCard
                    key={"defaultBenefit"}
                    title="Add"
                    onRenderFront={() => {
                        return (
                            <>
                                <p><Text>Add</Text></p>
                                <AddIcon style={{ fontSize: 'x-large', color: theme.palette.themePrimary }} />
                            </>
                        );
                    }}
                    onClick={(e) => {
                        props.onAdd?.()
                    }}
                />
            }
        </Stack>;
    }

    function onRenderBenefitGrid() {
        return <BenefitGrid benefits={benefits} />
    }


    return onRenderBenefit();
}

export default BenefitPanel;